import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import closeIcon from "../../assets/close.svg";
import "../../pdf-cart/trayComponent/tray.component.scss";
import "../../pdf-cart/userInformationFormComponent/userInformationForm.component.scss";
import "./returnScenariosWorkflow.component.scss";
import {
  IReturnScenariosWorkflowAvailability,
  IReturnScenariosWorkflowSelections,
  IReturnScenariosWorkflowUserInfo,
  ReturnScenariosSelectionKey,
} from "../types/states";

interface IReturnScenariosWorkflowProps {
  open: boolean;
  hasPdf: boolean;
  userInfo: IReturnScenariosWorkflowUserInfo;
  selections: IReturnScenariosWorkflowSelections;
  availability: IReturnScenariosWorkflowAvailability;
  errors: { [key: string]: string };
  errorMessage?: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onReset: () => void;
  onUserInfoChange: (field: string, value: string) => void;
  onDesignationToggle: (designation: string) => void;
  onSelectionToggle: (field: ReturnScenariosSelectionKey) => void;
  onViewPdf: () => void;
  onEmailPdf: () => void;
}

const DESIGNATIONS = [
  "APMA®",
  "BFA",
  "CFP®",
  "CFA®",
  "ChFC®",
  "CLU®",
  "CPA",
  "CRPC®",
  "CIMA®",
  "CMFC®",
];

const MAIN_SELECTION_OPTIONS: Array<{
  key: ReturnScenariosSelectionKey;
  label: string;
}> = [
  {
    key: 'section2',
    label: 'Show how indexed accounts work',
  },
  {
    key: 'section3',
    label: 'Show the value of protection',
  },
];

const ReturnScenariosWorkflow: React.FC<IReturnScenariosWorkflowProps> = ({
  open,
  hasPdf,
  userInfo,
  selections,
  availability,
  errors,
  errorMessage,
  submitting,
  onClose,
  onSubmit,
  onReset,
  onUserInfoChange,
  onDesignationToggle,
  onSelectionToggle,
  onViewPdf,
  onEmailPdf,
}) => {
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const trayRef = useRef<HTMLDivElement | null>(null);
  const successPrimaryButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousHasPdfRef = useRef(hasPdf);

  const portalTarget = typeof document !== "undefined" ? document.getElementById("pdf-tray") : null;

  const mainSelectionActive =
    (availability.section2 && selections.section2) ||
    (availability.section3 && selections.section3);
  const historicalEnabled = mainSelectionActive && availability.historical;

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!hasPdf && firstFieldRef.current) {
      window.requestAnimationFrame(() => {
        firstFieldRef.current && firstFieldRef.current.focus();
      });
    }
  }, [open, hasPdf]);

  useEffect(() => {
    if (!open) {
      previousHasPdfRef.current = hasPdf;
      return;
    }

    const transitionedToPdf = !previousHasPdfRef.current && hasPdf;
    previousHasPdfRef.current = hasPdf;

    if (!transitionedToPdf) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (successPrimaryButtonRef.current && typeof successPrimaryButtonRef.current.focus === 'function') {
        successPrimaryButtonRef.current.focus();
        return;
      }
      if (trayRef.current && typeof trayRef.current.focus === 'function') {
        trayRef.current.focus();
      }
    });
  }, [hasPdf, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  if (!open || !portalTarget) {
    return null;
  }

  const renderDesignation = (designation: string) => {
    if (designation.endsWith('®')) {
      return (
        <>
          {designation.slice(0, -1)}
          <sup>&reg;</sup>
        </>
      );
    }

    return designation;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!mainSelectionActive) {
      return;
    }
    onSubmit();
  };

  const renderForm = () => (
    <div className="pdf-tray--content">
      <form id="return-scenarios-user-info" onSubmit={handleSubmit}>
        <div className="row">
          <h3>Please enter the following information for your PDF:</h3>
        </div>
        <div className="row">
          <div className="ampri-form-input">
            <label htmlFor="clientName">
              Client name
            </label>
            <input
              ref={firstFieldRef}
              id="clientName"
              name="clientName"
              value={userInfo.clientName}
              onChange={(e) => onUserInfoChange("clientName", e.target.value)}
              aria-invalid={errors.clientName ? "true" : "false"}
              aria-describedby="clientNameHint"
            />
            <div className="return-scenarios-workflow__field-hint" id="clientNameHint">
              Optional. Adds the client's name to the cover page.
            </div>
            {errors.clientName && <div className="error-message">{errors.clientName}</div>}
          </div>
        </div>
        <div className="row">
          <div className="ampri-form-input">
            <label htmlFor="advisorName">Advisor name</label>
            <input
              id="advisorName"
              name="advisorName"
              value={userInfo.advisorName}
              onChange={(e) => onUserInfoChange("advisorName", e.target.value)}
              aria-invalid={errors.advisorName ? "true" : "false"}
              aria-describedby="advisorNameHint"
            />
            <div className="return-scenarios-workflow__field-hint" id="advisorNameHint">
              Optional. Appears under the advisor heading on the cover page.
            </div>
            {errors.advisorName && <div className="error-message">{errors.advisorName}</div>}
          </div>
        </div>
        <div className="row">
          <div className="ampri-form-input">
            <label htmlFor="advisorPhone">Advisor phone number</label>
            <input
              id="advisorPhone"
              name="advisorPhone"
              value={userInfo.advisorPhone}
              placeholder="(###) ###-####"
              onChange={(e) => onUserInfoChange("advisorPhone", e.target.value)}
              aria-invalid={errors.advisorPhone ? "true" : "false"}
              aria-describedby="advisorPhoneHint"
              type="tel"
              inputMode="tel"
              pattern="^\\(?\\d{3}\\)?[\\s.-]?\\d{3}[\\s.-]?\\d{4}$"
            />
            <div className="return-scenarios-workflow__field-hint" id="advisorPhoneHint">
              Optional. Enter a 10-digit phone number, with or without punctuation.
            </div>
          </div>
          {errorMessage && <div className="pdf-tray--error">{errorMessage}</div>}
        </div>
        <fieldset className="row row--flex">
          <legend>Designations</legend>
          <div className="designation-grid">
            {DESIGNATIONS.map((designation) => (
              <label key={designation} className="designation return-scenarios-workflow__checkbox">
                <input
                  type="checkbox"
                  checked={userInfo.designations.indexOf(designation) !== -1}
                  onChange={() => onDesignationToggle(designation)}
                />
                <span>{renderDesignation(designation)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="row">
          <div className="ampri-form-input">
            <label htmlFor="otherDesignations">Other designations</label>
            <input
              id="otherDesignations"
              name="otherDesignations"
              value={userInfo.otherDesignations}
              placeholder="Separate with a comma and a space"
              onChange={(e) => onUserInfoChange("otherDesignations", e.target.value)}
            />
          </div>
        </div>
        <fieldset className="row return-scenarios-workflow__selection" aria-labelledby="return-scenarios-report-selection">
          <legend id="return-scenarios-report-selection">Selected reports</legend>
          <div className="return-scenarios-workflow__selection-group">
            {MAIN_SELECTION_OPTIONS.map((option) => (
              <label
                key={option.key}
                className="return-scenarios-workflow__checkbox"
              >
                <input
                  type="checkbox"
                  checked={availability[option.key] && selections[option.key]}
                  disabled={!availability[option.key]}
                  onChange={() => onSelectionToggle(option.key)}
                  aria-checked={availability[option.key] && selections[option.key] ? 'true' : 'false'}
                  aria-disabled={!availability[option.key]}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
          <div className="return-scenarios-workflow__selection-group">
            <div className="return-scenarios-workflow__selection-subhead">Additional report:</div>
            <label className="return-scenarios-workflow__checkbox">
              <input
                type="checkbox"
                checked={historicalEnabled && selections.historical}
                disabled={!historicalEnabled}
                onChange={() => onSelectionToggle('historical')}
                aria-checked={historicalEnabled && selections.historical ? 'true' : 'false'}
                aria-disabled={!historicalEnabled}
              />
              <span>Include historical index results</span>
            </label>
          </div>
        </fieldset>
        <div className="pdf-tray--footer">
          <div className="pdf-tray--footer-buttons">
            <button
              type="submit"
              className="ampri-button-primary"
              disabled={submitting || !mainSelectionActive}
              data-automation="return-scenarios-workflow-submit"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
            <button
              type="button"
              className="ampri-button-tertiary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  const renderSuccess = () => (
    <div className="pdf-tray--links">
      <p>
        <i className="check"></i>Your PDF has been created.
      </p>
      <button
        className="ampri-button-primary"
        onClick={onViewPdf}
        ref={successPrimaryButtonRef}
      >
        View PDF
      </button>
      <button className="ampri-button-tertiary" onClick={onEmailPdf}>
        Email PDF
      </button>
      <button className="ampri-button-secondary" onClick={onReset}>
        Reset all inputs
      </button>
    </div>
  );

  const selectedCount =
    (availability.section2 && selections.section2 ? 1 : 0) +
    (availability.section3 && selections.section3 ? 1 : 0) +
    (historicalEnabled && selections.historical ? 1 : 0);
  const headerCount = hasPdf ? '✔' : (selectedCount ? selectedCount.toString() : '');

  return createPortal(
    <>
      <div
        ref={trayRef}
        tabIndex={-1}
        className={classNames("pdf-tray", {
          "pdf-tray--open": open,
          "pdf-tray--creating": submitting,
        })}
      >
        <div
          className={classNames("pdf-tray--header", {
            "pdf-tray--header--pdf": hasPdf,
          })}
        >
          <span>
            <i>
              <span>{headerCount}</span>
            </i>
            Your PDF
          </span>
          <span className="pdf-tray--close">
            <button type="button" onClick={onClose} data-automation="return-scenarios-workflow-close">
              <img src={`/dist/${closeIcon}`} alt="Close" />
            </button>
          </span>
        </div>
        {hasPdf ? renderSuccess() : renderForm()}
      </div>
      <div
        onClick={onClose}
        className={classNames('pdf-tray--screen-overlay', {
          'pdf-tray--screen-overlay-active': open,
        })}
      ></div>
    </>,
    portalTarget
  );
};

export default ReturnScenariosWorkflow;
